import { css } from '@emotion/react';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { parseSections } from '@sonematic/shared';

import { TeleprompterView } from '@/components/teleprompter/teleprompter-view';
import { useJoinSession } from '@/hooks/use-join-session';
import { LandingPage } from '@/pages/landing';
import { darkTheme } from '@/theme/dark-theme';
import { fontSizes, radii, shadows, spacing } from '@/theme/tokens';

function getCodeFromUrl(): string | null {
  const params = new URLSearchParams(window.location.search);
  return params.get('code');
}

export function App() {
  const session = useJoinSession();
  const autoJoinedRef = useRef(false);

  // Auto-join if code is in URL
  useEffect(() => {
    const code = getCodeFromUrl();
    if (code && !autoJoinedRef.current) {
      autoJoinedRef.current = true;
      session.join(code);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleJoin = useCallback(
    (code: string) => {
      session.join(code);
    },
    [session],
  );

  const handleReset = useCallback(() => {
    // Clear URL params
    window.history.replaceState({}, '', window.location.pathname);
    autoJoinedRef.current = false;
    session.reset();
  }, [session]);

  // Merge speaker notes from rawContent into sections
  const sectionsWithNotes = useMemo(() => {
    const project = session.projectData?.project;
    if (!project) return [];
    const parseResult = parseSections(project.rawContent);
    const notesByPosition = new Map(parseResult.sections.map((s) => [s.position, s.speakerNotes]));
    return project.sections.map((section) => ({
      ...section,
      speakerNotes: notesByPosition.get(section.position) ?? [],
    }));
  }, [session.projectData]);

  // idle: show landing page
  if (session.state === 'idle') {
    return <LandingPage onJoin={handleJoin} initialCode={getCodeFromUrl()} />;
  }

  // joining: show loading spinner
  if (session.state === 'joining') {
    return (
      <div
        css={css`
          min-height: 100vh;
          background: ${darkTheme.background};
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: ${spacing.md};
        `}
      >
        <div
          role="status"
          aria-label="Joining session"
          css={css`
            width: 32px;
            height: 32px;
            border: 3px solid ${darkTheme.border};
            border-top-color: ${darkTheme.primary};
            border-radius: 50%;
            animation: spin 0.8s linear infinite;

            @keyframes spin {
              to {
                transform: rotate(360deg);
              }
            }
          `}
        />
        <p
          css={css`
            color: ${darkTheme.textSecondary};
            font-size: ${fontSizes.sm};
            margin: 0;
          `}
        >
          Joining session...
        </p>
      </div>
    );
  }

  // error: show error with retry
  if (session.state === 'error') {
    return (
      <div
        css={css`
          min-height: 100vh;
          background: ${darkTheme.background};
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: ${spacing.md};
          padding: ${spacing.lg};
        `}
      >
        <p
          role="alert"
          css={css`
            color: ${darkTheme.error};
            font-size: ${fontSizes.md};
            margin: 0;
            text-align: center;
          `}
        >
          {session.error}
        </p>
        <button
          type="button"
          onClick={handleReset}
          css={css`
            padding: ${spacing.sm} ${spacing.lg};
            border-radius: ${radii.button};
            background: ${darkTheme.buttonBg};
            color: ${darkTheme.primaryText};
            font-weight: 700;
            font-size: ${fontSizes.sm};
            text-transform: uppercase;
            letter-spacing: 0.2px;
            cursor: pointer;
            min-height: 44px;
            border: 1px solid ${darkTheme.buttonBorder};
            box-shadow: ${shadows.insetButton};
            transition: opacity 0.15s;

            &:hover {
              opacity: 0.9;
            }
          `}
        >
          Try Again
        </button>
      </div>
    );
  }

  // ended: show session ended
  if (session.state === 'ended') {
    return (
      <div
        css={css`
          min-height: 100vh;
          background: ${darkTheme.background};
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: ${spacing.md};
          padding: ${spacing.lg};
        `}
      >
        <p
          css={css`
            color: ${darkTheme.text};
            font-size: ${fontSizes.lg};
            margin: 0;
            text-align: center;
            font-weight: 600;
          `}
        >
          Session Ended
        </p>
        <p
          css={css`
            color: ${darkTheme.textSecondary};
            font-size: ${fontSizes.sm};
            margin: 0;
            text-align: center;
          `}
        >
          The presenter has ended this session.
        </p>
        <button
          type="button"
          onClick={handleReset}
          css={css`
            margin-top: ${spacing.md};
            padding: ${spacing.sm} ${spacing.lg};
            border-radius: ${radii.button};
            background: ${darkTheme.buttonBg};
            color: ${darkTheme.primaryText};
            font-weight: 700;
            font-size: ${fontSizes.sm};
            text-transform: uppercase;
            letter-spacing: 0.2px;
            cursor: pointer;
            min-height: 44px;
            border: 1px solid ${darkTheme.buttonBorder};
            box-shadow: ${shadows.insetButton};
            transition: opacity 0.15s;

            &:hover {
              opacity: 0.9;
            }
          `}
        >
          Join Another
        </button>
      </div>
    );
  }

  // active: show teleprompter
  if (!session.projectData) return null;

  const audioSections = session.projectData.audioSections.map((s) => ({
    position: s.position,
    audioUrl: s.audioUrl,
    audioDurationMs: s.audioDurationMs,
    generationStatus: s.generationStatus,
    estimatedDurationMs: null,
  }));

  return (
    <TeleprompterView
      sections={sectionsWithNotes}
      audioSections={audioSections}
      socket={session.socket}
      sessionId={session.sessionId}
      sessionSettings={session.sessionSettings}
    />
  );
}
